/**
 * Interface language. English text is the key; each language is a set of JSON dictionaries in ./<lang>/
 * (one per screen area, so they can be edited separately). Anything missing falls back to English.
 *
 *   t("Hire")                         -> "Найняти"
 *   t("Week {n}", { n: 4 })           -> "Тиждень 4"
 *   tp(3, "{n} game", "{n} games")    -> "3 гри" (Ukrainian dictionaries give three forms: "{n} гра|{n} гри|{n} ігор")
 *
 * Game content prose (events, check-ins, reviews, news) stays English for now; names of mechanics, themes,
 * rooms and the like go through t() like any other label.
 */
import { setTranslator } from "@bd/ui-kit";

export type Lang = "en" | "uk";
export const LANGS: { id: Lang; name: string }[] = [
  { id: "en", name: "English" },
  { id: "uk", name: "Українська" },
];

const files = import.meta.glob("./uk/*.json", { eager: true, import: "default" }) as Record<string, Record<string, string>>;
const UK: Record<string, string> = Object.assign({}, ...Object.values(files));
/** Ukrainian already: a label translated once and passed through t() again stays as it is. */
const UK_TEXT = new Set(Object.values(UK));

function initialLang(): Lang {
  try {
    const q = new URLSearchParams(location.search).get("lang");
    if (q === "uk" || q === "en") return q;
    const saved = localStorage.getItem("bd:lang");
    if (saved === "uk" || saved === "en") return saved;
  } catch {
    // storage blocked: English
  }
  return "en";
}

let lang: Lang = initialLang();
/** In Ukrainian, every English key asked for that has no translation (QA reads this to find gaps). */
export const missing = new Set<string>();

export function currentLang(): Lang {
  return lang;
}

/** Switch language; the store keeps a copy so the interface draws again. */
export function setLang(l: Lang): void {
  lang = l;
  try {
    localStorage.setItem("bd:lang", l);
  } catch {
    // storage blocked: this visit only
  }
  document.documentElement.lang = l;
}

function fill(s: string, vars?: Record<string, string | number>): string {
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s;
}

/**
 * Translate one piece of interface text (English in, the player's language out). A word with two meanings takes a
 * context: t("Space", undefined, "key") looks up "key::Space" first (the space bar, not the theme).
 */
export function t(en: string, vars?: Record<string, string | number>, ctx?: string): string {
  if (lang === "en" || !en) return fill(en, vars);
  const s = (ctx ? UK[`${ctx}::${en}`] : undefined) ?? UK[en];
  if (s === undefined) {
    if (!UK_TEXT.has(en)) missing.add(en);
    return fill(en, vars);
  }
  return fill(s, vars);
}

/** A date label from the rules engine ("Winter 2024") in the player's language ("Зима 2024"). */
export function tdate(label: string): string {
  const [season, ...rest] = label.split(" ");
  return [t(season), ...rest].join(" ");
}

/**
 * Text the rules engine has already filled in ("Ana leaves for Neon Rook."): find the dictionary template it came from
 * ("{name} leaves for {rival}.") and give back the Ukrainian with the same pieces. Pieces that are themselves in the
 * dictionary (a theme, a room, a season) are translated too; names stay as they are. Plain text works like t().
 *   tx("Ana leaves for Neon Rook.") -> "Ана йде до Neon Rook."
 * Every dictionary key with {placeholders} is a template. Longer templates are tried first, so the most specific wins.
 */
let compiled: { re: RegExp; names: string[]; uk: string; literal: number }[] | null = null;
const txCache = new Map<string, string>();
function patterns() {
  if (compiled) return compiled;
  compiled = Object.entries(UK)
    .filter(([k]) => k.includes("{") && !k.includes("|") && !k.includes("::") && /\{\w+\}/.test(k))
    .sort((a, b) => b[0].replace(/\{\w+\}/g, "").length - a[0].replace(/\{\w+\}/g, "").length)
    .map(([k, uk]) => {
      const names: string[] = [];
      const src = k.split(/(\{\w+\})/).map((part) => {
        const m = /^\{(\w+)\}$/.exec(part);
        if (m) {
          names.push(m[1]);
          return "(.+?)";
        }
        return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      }).join("");
      return { re: new RegExp(`^${src}$`, "s"), names, uk, literal: k.replace(/\{\w+\}/g, "").length };
    });
  return compiled;
}

function piece(v: string): string {
  const trimmed = v.trim();
  const hit = UK[trimmed];
  if (hit !== undefined) return v.replace(trimmed, hit);
  // a date the engine wrote ("Spring 1984")
  const d = /^(Winter|Spring|Summer|Autumn) (\d{4})$/.exec(trimmed);
  if (d && UK[d[1]]) return `${UK[d[1]]} ${d[2]}`;
  return v;
}

export function tx(en: string): string {
  if (lang === "en" || !en) return en;
  // numbers, scores and money need no translation
  if (/^[\d\s.,%$+−\-–×/:#~KM]+$/.test(en)) return en;
  const direct = UK[en];
  if (direct !== undefined) return direct;
  if (UK_TEXT.has(en)) return en;
  const cached = txCache.get(en);
  if (cached !== undefined) return cached;
  let out: string | undefined;
  for (const p of patterns()) {
    // a short template ("{a} of {b}") may only stand for a short line: its own words must be a fair share of the text
    if (p.literal < 12 && p.literal < en.length * 0.3) continue;
    const m = p.re.exec(en);
    if (!m) continue;
    const vars: Record<string, string> = {};
    p.names.forEach((n, i) => (vars[n] = piece(m[i + 1])));
    out = fill(p.uk, vars);
    break;
  }
  if (out === undefined) {
    missing.add(en);
    out = en;
  }
  if (txCache.size > 4000) txCache.clear();
  txCache.set(en, out);
  return out;
}

/** Ukrainian plural form: 1 гра, 2 гри, 5 ігор (and 21 гра, 22 гри, 11 ігор). */
function ukForm(n: number): 0 | 1 | 2 {
  const a = Math.abs(Math.trunc(n));
  if (a % 10 === 1 && a % 100 !== 11) return 0;
  if (a % 10 >= 2 && a % 10 <= 4 && (a % 100 < 12 || a % 100 > 14)) return 1;
  return 2;
}

/** A count with the right plural: tp(n, "{n} game", "{n} games"). {n} is filled with the number. */
export function tp(n: number, one: string, many: string, vars?: Record<string, string | number>): string {
  const v = { n: n.toLocaleString(lang === "uk" ? "uk-UA" : "en-US"), ...vars };
  if (lang === "en") return fill(n === 1 ? one : many, v);
  const s = UK[`${one}|${many}`];
  if (s === undefined) {
    missing.add(`${one}|${many}`);
    return fill(n === 1 ? one : many, v);
  }
  const forms = s.split("|");
  return fill(forms[Math.min(forms.length - 1, ukForm(n))], v);
}

setTranslator(t);
document.documentElement.lang = lang;
(window as unknown as { __bdI18n: unknown }).__bdI18n = { missing, currentLang };
